import React, { useEffect, useMemo, useState } from 'react';
import api from '@/services/api';

interface CredentialResponse {
  email: string;
  password: string;
  name?: string;
  purpose?: string;
  backupCodes?: string[];
}

type Status = 'loading' | 'success' | 'expired' | 'error';

const Welcome: React.FC = () => {
  const [status, setStatus] = useState<Status>('loading');
  const [credentials, setCredentials] = useState<CredentialResponse | null>(null);

  const token = useMemo(() => {
    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    return params.get('token');
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }

    const consumeLink = async () => {
      try {
        window.history.replaceState({}, document.title, '/welcome');
        const response = await api.client.post('/external-partners/credentials/consume', { token });
        setCredentials(response.data.data as CredentialResponse);
        setStatus('success');
      } catch (error: any) {
        if (error?.response?.status === 410) {
          setStatus('expired');
        } else {
          setStatus('error');
        }
      }
    };

    consumeLink();
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-lg bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4 text-gray-900">
          <i className="fas fa-key text-blue-600" />
          <h1 className="text-lg font-semibold">Account Credentials</h1>
        </div>

        {status === 'loading' && (
          <div className="text-sm text-gray-600">Loading secure link...</div>
        )}

        {status === 'expired' && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            This link has expired or was already used. Please contact your administrator for a new link.
          </div>
        )}

        {status === 'error' && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            This link is invalid. Please contact your administrator.
          </div>
        )}

        {status === 'success' && credentials && (
          <div className="space-y-4">
            {/* One-time warning banner */}
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <i className="fas fa-exclamation-triangle text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    Save These Credentials Now
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    This is a <strong>one-time view</strong>. Once you leave this page, these credentials
                    cannot be displayed again. Please copy or write them down before continuing.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600">
              Use the credentials below to sign in. You will be prompted to change your password on first login.
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500 w-20">Email:</span>
                <span className="font-mono bg-gray-50 px-2 py-1 rounded border flex-1 truncate">
                  {credentials.email}
                </span>
                <button
                  onClick={() => navigator.clipboard.writeText(credentials.email)}
                  className="text-blue-600 hover:text-blue-800"
                  aria-label="Copy email"
                >
                  <i className="fas fa-copy" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 w-20">Password:</span>
                <span className="font-mono bg-gray-50 px-2 py-1 rounded border flex-1 truncate">
                  {credentials.password}
                </span>
                <button
                  onClick={() => navigator.clipboard.writeText(credentials.password)}
                  className="text-blue-600 hover:text-blue-800"
                  aria-label="Copy password"
                >
                  <i className="fas fa-copy" />
                </button>
              </div>
            </div>

            {/* 2FA Backup Codes Section */}
            {credentials.backupCodes && credentials.backupCodes.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fas fa-shield-halved text-purple-600" />
                  <h3 className="text-sm font-semibold text-purple-900">
                    2-Step Verification Backup Codes
                  </h3>
                </div>
                <p className="text-xs text-purple-700 mb-3">
                  Your organization requires 2-step verification. Use one of these codes on your first login when prompted.
                </p>
                <div className="bg-white border border-purple-200 rounded p-3 font-mono text-sm space-y-1">
                  {credentials.backupCodes.map((code, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-gray-800">{code}</span>
                      <button
                        onClick={() => navigator.clipboard.writeText(code)}
                        className="text-purple-600 hover:text-purple-800 text-xs"
                        aria-label={`Copy backup code ${index + 1}`}
                      >
                        <i className="fas fa-copy" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-purple-600 mt-2">
                  <i className="fas fa-info-circle mr-1" />
                  Each code can only be used once. Save all codes securely.
                </p>
              </div>
            )}

            {/* Login link */}
            <div className="pt-2 border-t border-gray-100">
              <a
                href="https://deposits.proviarcm.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <i className="fas fa-arrow-right-to-bracket" />
                Go to Login Page
              </a>
              <p className="text-xs text-center text-gray-500 mt-2">
                deposits.proviarcm.com
              </p>
            </div>

            {/* Final reminder */}
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 text-center">
              <i className="fas fa-lock mr-1" />
              Keep this information secure. This page will not be accessible again.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Welcome;
